import StoreCheckout from 'GlobalStores/Checkout/StoreCheckout'
import storeCountry from 'GlobalStores/Cores/County/StoreCountry'
import { get } from 'lodash'
import ModalSelect from '../ModalSelect/ModalSelect'
import ModalSearch from '../ModalSearch/ModalSearch'
import DefaultInput from '../DefaultInput'
import { DefaultLabelTypo } from '../InputsStyled'
import { InputAdornment, Typography } from '@mui/material'
import JPNModalSearch from 'Components/Elements/Checkout/ShippingAddressForm/Country/Japan/JPNModalSearch'
import { callOnChangeOrSetCheckout, handleBlurRemoveSpaces, handleDefaultInputChange, preventEmailSpace, resolveDefaultInputType, sanitizeNumericValue, shouldRenderCitySelect, shouldRenderStateSelect, shouldRenderThaiFullName } from './fieldHelpers'

const renderJapanZipCodeField = ({ translate, item, onChange }) => (
    <JPNModalSearch
        label={translate(item.key)}
        value={item.value}
        onChange={event => {
            const allowNumber = sanitizeNumericValue(event.target.value)
            callOnChangeOrSetCheckout(item, allowNumber, onChange)
        }}
    />
)

const renderThailandZipCodeField = ({ translate, item, onChange }) => {
    const options = []

    return (
        <ModalSearch
            label={translate(item.key)}
            onSet={value => {
                StoreCheckout.CheckOutInstance().MapAutoAddressToForm(StoreCheckout.GetShippingForm(), value)
                StoreCheckout.CheckOutInstance().MapAutoAddressToForm(StoreCheckout.GetCheckoutForms(), value)
            }}
            options={options}
            value={item.value}
            onChange={event => {
                const allowNumber = sanitizeNumericValue(event.target.value)
                callOnChangeOrSetCheckout(item, allowNumber, onChange)
            }}
            itemKeys={StoreCheckout.CheckOutInstance().AutoAddressDisplayKeys()}
        />
    )
}

const renderStateField = ({ translate, item }) => {
    const options = []

    StoreCheckout.CheckOutInstance()
        .Instance()
        .StatesList()
        .forEach(stateItem => options.push(stateItem.value))

    const value = item.value ? item.value.toUpperCase() : ''
    const placeholder = ['SAU'].includes(storeCountry.Country3()) ? translate(get(item, 'placeholder', '') || 'select_your_district') : ''

    return (
        <ModalSelect
            label={translate(item.key)}
            onSet={value => {
                StoreCheckout.SetCheckoutFormValue(item.key, value)
            }}
            placeholder={placeholder}
            options={options}
            value={value}
        />
    )
}

const renderCitySelect = ({ translate, item }) => {
    const options = StoreCheckout.CheckOutInstance()
        .Instance()
        .getCityOptionList()
        .map(option => option.value)

    const value = item.value ? item.value.toUpperCase() : ''

    return (
        <ModalSelect
            label={translate(item.key)}
            onSet={value => {
                StoreCheckout.SetCheckoutFormValue(item.key, value)
            }}
            options={options}
            value={value}
        />
    )
}

const renderProvinceField = context => {
    const { translate } = context

    const label = (
        <DefaultLabelTypo variant="label">
            {translate('fg_province')}
        </DefaultLabelTypo>
    )

    return renderGenericInput({ ...context, labelComponent: label })
}

const renderThaiFullNameInput = context => {
    const { translate, item } = context

    const label = (
        <DefaultLabelTypo variant="label">
            {translate(item.label)}
        </DefaultLabelTypo>
    )

    return renderGenericInput({ ...context, labelComponent: label, disableOptionalLabel: true })
}

export const translateFieldLabel = (translate, item) => {
    const rawLabel = item.label || item.key
    const labelKey = rawLabel === 'Mobile Number' ? 'mobile_number' : rawLabel
    const translatedLabel = translate(labelKey)

    if (translatedLabel !== labelKey || labelKey === item.key) return translatedLabel

    const keyTranslation = translate(item.key)
    return keyTranslation !== item.key ? keyTranslation : rawLabel
}

const renderGenericInput = ({ translate, item, onChange, helpertextcardtype, formattedValue, onKeyUp, inputType, labelComponent, disableOptionalLabel, onChangeHandler, onBlurHandler, startAdornment }) => {
    const label = labelComponent || (
        <DefaultLabelTypo variant="label">
            {translateFieldLabel(translate, item)}
            {item.showRequiredMark ? <span> *</span> : null} {item.optional && !disableOptionalLabel ? <span style={{ fontSize: 10 }}>({translate('optional')})</span> : null}
        </DefaultLabelTypo>
    )

    const value = item.key === 'card_number' ? formattedValue : item.value

    const handleChange = onChangeHandler ? onChangeHandler : event => handleDefaultInputChange({ item, event, onChange })

    const handleBlur = onBlurHandler || (event => handleBlurRemoveSpaces(item, event))

    const adornment =
        startAdornment ||
        (item.key === 'phone' && ['SAU'].includes(storeCountry.Country3()) ? (
            <InputAdornment position="start" sx={{ position: 'absolute', left: '18px' }}>
                <Typography
                    sx={{
                        fontSize: '14px',
                        fontWeight: '400',
                        color: '#153862',
                        fontFamily: 'Inter, NotoSansThai'
                    }}>
                    +{StoreCheckout.CheckOutInstance().Instance().prefixPhoneNumber}
                </Typography>
            </InputAdornment>
        ) : null)

    return (
        <DefaultInput
            helpertextcardtype={item.key === 'card_number' && helpertextcardtype}
            label={label}
            type={inputType || resolveDefaultInputType(item)}
            value={value}
            key={item.key}
            typeKey={item.key}
            disabled={item.disabled || false}
            placeholder={item.placeholder || ''}
            onKeyUp={event => {
                if (onKeyUp) onKeyUp(item, event)
            }}
            onChange={handleChange}
            onKeyDown={event => preventEmailSpace(item, event)}
            onBlur={handleBlur}
            startAdornment={adornment}
            {...item.inputProps}
            style={{ WebkitTextSecurity: ['cvv'].includes(item.key) ? 'disc' : 'none' }}
        />
    )
}

const renderDefaultField = context => {
    const { item } = context
    const inputType = resolveDefaultInputType(item)

    if (item.key === 'zip_code' && storeCountry.Country3() === 'JPN') {
        return renderJapanZipCodeField(context)
    }

    if (item.key === 'zip_code' && storeCountry.Country3() === 'THA') {
        return renderThailandZipCodeField(context)
    }

    if (item.key === 'province') {
        return renderProvinceField({ ...context, inputType })
    }

    if (shouldRenderStateSelect(item)) {
        return renderStateField(context)
    }

    if (shouldRenderCitySelect(item)) {
        return renderCitySelect(context)
    }

    if (shouldRenderThaiFullName(item)) {
        return renderThaiFullNameInput({ ...context, inputType })
    }

    return renderGenericInput({ ...context, inputType })
}

export default renderDefaultField

export { renderGenericInput }
